// Spins the lucky wheel with requestAnimationFrame.
// start() spins the wheel up straight away, so the player sees a response before the server answers.
// stopAt() then eases out from the current angle and speed and lands exactly on the prize angle.

const TOP_SPEED = 0.72 // degrees per millisecond (two turns a second)
const SPIN_UP_MS = 400
const MIN_TURNS = 2 // full turns still to go once the result is known
const ERROR_STOP_MS = 600

// Give up waiting for the spin request after this long, stop the wheel and tell the player.
export const SPIN_TIMEOUT_MS = 15000

export class WheelSpinner {
  constructor() {
    this.el = null
    this.angle = 0
    this.speed = 0
    this.phase = 'idle' // idle | spinning | stopping
    this.frame = null
    this.pendingStop = null
  }

  attach(el) {
    this.el = el
    this.render()
  }

  start() {
    if (this.phase !== 'idle') return
    this.phase = 'spinning'
    this.speed = 0
    const startTime = performance.now()
    let last = startTime
    const tick = now => {
      const dt = now - last
      last = now
      this.speed = TOP_SPEED * Math.min(1, (now - startTime) / SPIN_UP_MS)
      this.angle += this.speed * dt
      this.render()
      // only begin slowing down once at full speed, so the speed never jumps
      if (this.pendingStop && this.speed === TOP_SPEED) {
        const stop = this.pendingStop
        this.pendingStop = null
        this.easeOut(stop, now)
        return
      }
      this.frame = requestAnimationFrame(tick)
    }
    this.frame = requestAnimationFrame(tick)
  }

  // Land on targetAngle (any value; only its position on the circle matters).
  stopAt(targetAngle, onDone) {
    if (this.phase === 'idle') this.start()
    this.requestStop(targetAngle, onDone)
  }

  // Stop quickly without aiming at a prize (used when the spin request fails).
  stop(onDone) {
    if (this.phase === 'idle') return onDone && onDone()
    this.requestStop(null, onDone)
  }

  requestStop(targetAngle, onDone) {
    if (this.phase === 'stopping') return
    this.pendingStop = { targetAngle, onDone }
  }

  // The distance is worked out here, from the angle at the moment the slow-down really starts.
  easeOut({ targetAngle, onDone }, startTime) {
    this.phase = 'stopping'
    const from = this.angle
    let distance
    if (targetAngle === null) {
      // ease-out cubic starts at 3x its average speed, so this distance keeps the speed continuous
      distance = (TOP_SPEED * ERROR_STOP_MS) / 3
    } else {
      const turnsEnd = from + MIN_TURNS * 360
      distance = MIN_TURNS * 360 + ((((targetAngle - turnsEnd) % 360) + 360) % 360)
    }
    // ease-out cubic: initial speed is 3 * distance / duration, so pick the duration that matches the current speed
    const duration = (3 * distance) / this.speed
    const tick = now => {
      const p = Math.min(1, (now - startTime) / duration)
      this.angle = from + distance * (1 - Math.pow(1 - p, 3))
      this.render()
      if (p < 1) {
        this.frame = requestAnimationFrame(tick)
        return
      }
      this.angle %= 360
      this.speed = 0
      this.phase = 'idle'
      this.frame = null
      this.render()
      if (onDone) onDone()
    }
    this.frame = requestAnimationFrame(tick)
  }

  render() {
    if (this.el) this.el.style.transform = `rotate(${this.angle}deg)`
  }

  destroy() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    this.pendingStop = null
    this.el = null
  }
}
